import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { ActionType, DeliveryOption } from '@/constants';
import { useGetShippingOptions } from '@/hooks/useGetShippingOptions';
import { useDataLayer } from '@/hooks/useDataLayer';
import type { AddressPayload, DeliveryOptionType } from '@/types';
import { useCheckout } from './checkoutContext';
import { orderPayloadReducer } from './orderPayloadReducer';
import type {
  ProviderProps,
  ShippingContextValue,
  ShippingOption,
} from './types';
import { useUser } from './userContext';
import { useSetDeliveryCountry } from '@/hooks/useSetDeliveryCountry';

const ShippingContext = createContext<ShippingContextValue | null>(null);

const initialAddress = {
  firstName: '',
  lastName: '',
  company: '',
  line1: '',
  line2: '',
  city: '',
  countyCode: '',
  countyName: '',
  postalCode: '',
  countryCode: 'IRL',
  countryName: 'Ireland',
  phone: '',
};

const initialOrderPayload = {
  billingAddress: initialAddress,
  clickAndCollectStoreId: '',
  deliveryOption: '',
  email: '',
  emailOptInNewsAndOffers: false,
  giftMessage: '',
  isGift: false,
  shippingAddress: initialAddress,
} as const;

export const ShippingProvider = ({ children, isCheckoutPage = false }: ProviderProps) => {
  const { checkoutData } = useCheckout();
  const { isClickAndCollectAvailable, shippingMethod } = checkoutData;
  const { addShippingInfo } = useDataLayer();
  const { email } = useUser();
  const [storedAddressIds, setStoredAddressIds] = useState<string[]>([]);
  const [isBillingSameAsShipping, setIsBillingSameAsShipping] = useState(true);
  const hasTrackedAutoDelivery = useRef(false);
  const hasInitializedDeliveryOption = useRef(false);

  const [orderPayload, dispatch] = useReducer(
    orderPayloadReducer,
    initialOrderPayload,
  );

  const shippingOptions = useGetShippingOptions(
    orderPayload.shippingAddress.countryCode,
  );
  const mappedShippingOptions = shippingOptions?.map(
    (option: ShippingOption) => ({
      description: option.description,
      label: option.title,
      price: option.price,
      value: option.type,
    }),
  );

  const setBillingAddress = useCallback((value: Partial<AddressPayload>) => {
    dispatch({
      type: ActionType.SetBillingAddress,
      payload: value,
    });
  }, []);

  const setClickAndCollectStoreId = useCallback((value: string) => {
    dispatch({
      type: ActionType.SetClickAndCollectStoreId,
      payload: value,
    });
  }, []);

  const setDeliveryAddress = useCallback((value: Partial<AddressPayload>) => {
    dispatch({
      type: ActionType.SetDeliveryAddress,
      payload: value,
    });
  }, []);

  const setDeliveryOption = useCallback((value: DeliveryOptionType) => {
    dispatch({
      type: ActionType.SetDeliveryOption,
      payload: value,
    });
  }, []);

  const setEmail = useCallback((value: string) => {
    dispatch({ type: ActionType.SetEmail, payload: value });
  }, []);

  const setGiftMessage = useCallback((value: string) => {
    dispatch({ type: ActionType.SetGiftMessage, payload: value });
  }, []);

  const setIsGift = useCallback((value: boolean) => {
    dispatch({ type: ActionType.SetIsGift, payload: value });
  }, []);

  const setMarketingOptIn = useCallback((value: boolean) => {
    dispatch({ type: ActionType.SetMarketingOptIn, payload: value });
  }, []);

  const { setDeliveryCountry } = useSetDeliveryCountry();

  useEffect(() => {
    setDeliveryCountry({
      countryCode: orderPayload.shippingAddress.countryCode,
    });
  }, [orderPayload.shippingAddress.countryCode, setDeliveryCountry]);

  useEffect(() => {
    if (email) {
      setEmail(email);
    }
  }, [email, setEmail]);

  useEffect(() => {
    // Only initialize delivery option once to prevent resetting user's selection
    // when checkoutData.items changes (e.g., from cart refetch)
    if (hasInitializedDeliveryOption.current) {
      return;
    }

    if (isClickAndCollectAvailable) {
      setDeliveryOption('');
      hasTrackedAutoDelivery.current = false;
    } else {
      setDeliveryOption(DeliveryOption.Ship);
      // Track the automatic Delivery selection only once on checkout page
      if (isCheckoutPage && !hasTrackedAutoDelivery.current) {
        addShippingInfo('Delivery', checkoutData.items || []);
        hasTrackedAutoDelivery.current = true;
      }
    }

    hasInitializedDeliveryOption.current = true;
  }, [isClickAndCollectAvailable, setDeliveryOption, addShippingInfo, checkoutData.items, isCheckoutPage]);

  const isClickAndCollect =
    orderPayload.deliveryOption === DeliveryOption.Collect;

  const isDelivery = orderPayload.deliveryOption === DeliveryOption.Ship;

  const value = useMemo(
    () => ({
      initialAddress,
      isBillingSameAsShipping,
      isClickAndCollect,
      isDelivery,
      orderPayload:
        isDelivery && isBillingSameAsShipping
          ? { ...orderPayload, billingAddress: orderPayload.shippingAddress }
          : orderPayload,
      setBillingAddress,
      setClickAndCollectStoreId,
      setDeliveryAddress,
      setDeliveryOption,
      setEmail,
      setGiftMessage,
      setIsBillingSameAsShipping,
      setIsGift,
      setMarketingOptIn,
      setStoredAddressIds,
      shippingMethod,
      shippingOptions: mappedShippingOptions,
      storedAddressIds,
    }),
    [
      isBillingSameAsShipping,
      isClickAndCollect,
      isDelivery,
      mappedShippingOptions,
      orderPayload,
      setBillingAddress,
      setClickAndCollectStoreId,
      setDeliveryAddress,
      setDeliveryOption,
      setEmail,
      setGiftMessage,
      setIsGift,
      setMarketingOptIn,
      shippingMethod,
      storedAddressIds,
    ],
  );

  return (
    <ShippingContext.Provider value={value}>
      {children}
    </ShippingContext.Provider>
  );
};

export const useShipping = () => {
  const context = useContext(ShippingContext);

  if (context === undefined || context === null) {
    throw new Error(
      'useShipping must be used within a ShippingContext Provider',
    );
  }

  return context;
};
